// A caption's words when its text is typed or split in the Transcript (#239, spec
// docs/specs/editor/captions.md, "Typing" and "Enter"): live's re-alignment (read for behaviour,
// never copied), the words timed over the caption's own length (decision 1), and live's Enter
// split with the second part's words kept at their time (decision 5). Pure: the Transcript calls it.
import {
  MAX_CAPTION_LINES,
  MAX_CAPTION_WORDS,
  cutWord,
  linesOf,
  wordsIn,
  type CaptionWord,
} from "./words";

/** A typed word's time at least, and an Enter split's parts' at least. */
const MIN_WORD_MS = 100;
const MIN_PART_MS = 100;

/** Live's match scores: the same word; the same once case, tags and edge punctuation are set aside;
 * one beginning the other; one inside the other (three characters or more for those two). */
const SAME = 10;
const SAME_CLEANED = 8;
const PREFIX = 5;
const INSIDE = 4;
const MIN_PARTIAL_CHARS = 3;

const clamp = (value: number, low: number, high: number): number => Math.min(high, Math.max(low, value));

interface Typed {
  text: string;
  line: number;
}

/** The text's words, each on its line: lines split on line breaks (a blank one still counts), a
 * line's words on spaces; within the contract's bounds (100 words, 100 characters a word, a line
 * past the tenth joining the tenth). */
function typedWords(text: string): Typed[] {
  const words: Typed[] = [];
  text.split(/\r?\n/u).forEach((lineText, line) => {
    // The server's blank characters split words, control characters never in one (words.ts).
    for (const word of wordsIn(lineText)) {
      words.push({ text: cutWord(word), line: Math.min(line, MAX_CAPTION_LINES - 1) });
    }
  });
  return words.slice(0, MAX_CAPTION_WORDS);
}

/** A word with its case, `<tags>` and leading and trailing punctuation set aside. */
const cleaned = (text: string): string =>
  text
    .toLowerCase()
    .replace(/<[^>]*>/gu, "")
    .replace(/^\p{P}+|\p{P}+$/gu, "");

/** How well an old word stands for a typed one (0: not at all). */
function matchScore(old: string, typed: string): number {
  if (old === typed) return SAME;
  const a = cleaned(old);
  const b = cleaned(typed);
  if (a === "" || b === "") return 0;
  if (a === b) return SAME_CLEANED;
  if (a.length < MIN_PARTIAL_CHARS || b.length < MIN_PARTIAL_CHARS) return 0;
  if (a.startsWith(b) || b.startsWith(a)) return PREFIX;
  if (a.includes(b) || b.includes(a)) return INSIDE;
  return 0;
}

/**
 * Which old word each typed word keeps the time of (-1: none), by the best total score over the
 * words in order (a longest-common-subsequence table). Of equally good alignments, the one that
 * matches earliest wins: a word is matched where it can be, else an old word is passed over before
 * a typed one.
 */
function align(old: readonly CaptionWord[], typed: readonly Typed[]): number[] {
  const rows = old.length;
  const cols = typed.length;
  const scores = old.map((word) => typed.map((other) => matchScore(word.text, other.text)));
  // best[i][j]: the best total for the old words from i and the typed words from j on.
  const best = Array.from({ length: rows + 1 }, () => Array.from({ length: cols + 1 }, () => 0));
  for (let i = rows - 1; i >= 0; i--) {
    for (let j = cols - 1; j >= 0; j--) {
      const score = scores[i]![j]!;
      best[i]![j] = Math.max(best[i + 1]![j]!, best[i]![j + 1]!, score > 0 ? score + best[i + 1]![j + 1]! : 0);
    }
  }
  const matched = Array.from({ length: cols }, () => -1);
  let i = 0;
  let j = 0;
  while (i < rows && j < cols) {
    const score = scores[i]![j]!;
    if (score > 0 && best[i]![j] === score + best[i + 1]![j + 1]!) {
      matched[j] = i;
      i++;
      j++;
    } else if (best[i]![j] === best[i + 1]![j]) i++;
    else j++;
  }
  return matched;
}

/** `count` words sharing `from` to `to` evenly, each at least `least` ms, in whole ms. */
function shared(from: number, to: number, count: number, least: number): { startMs: number; endMs: number }[] {
  const each = Math.max(least, (to - from) / count);
  return Array.from({ length: count }, (_, at) => ({
    startMs: Math.round(from + at * each),
    endMs: Math.round(from + (at + 1) * each),
  }));
}

/**
 * A caption's words for its text as typed (live's re-alignment): the same words on the same lines
 * give the old words back. Else the typed words are matched to the old (see `align`); a matched
 * word keeps its old time, and each run of new words shares the time from the matched word before
 * it (or the caption's start) to the one after it (or the caption's end) evenly, at least 100 ms a
 * word; with no old words, the words share the caption evenly. Then a word starting before the one
 * before it ends starts at that end (100 ms long if that leaves it none), and every time is held
 * within the caption, `lengthMs` long (live's timed them over 5 s, whatever its length, and let them
 * run past it).
 */
export function realign(old: readonly CaptionWord[], text: string, lengthMs: number): CaptionWord[] {
  const length = Math.max(0, Math.round(lengthMs));
  const typed = typedWords(text);
  const unchanged =
    typed.length === old.length && typed.every((word, at) => word.text === old[at]!.text && word.line === old[at]!.line);
  if (unchanged) return [...old];
  if (typed.length === 0) return [];
  if (old.length === 0) {
    const times = shared(0, length, typed.length, 0);
    return typed.map((word, at) => ({ ...word, ...times[at]!, emphasis: null }));
  }

  const matched = align(old, typed);
  const times: { startMs: number; endMs: number }[] = [];
  for (let at = 0; at < typed.length; ) {
    const oldAt = matched[at]!;
    if (oldAt >= 0) {
      times.push({ startMs: old[oldAt]!.startMs, endMs: old[oldAt]!.endMs });
      at++;
      continue;
    }
    // A run of new words, up to the next matched word (or the end).
    let end = at;
    while (end < typed.length && matched[end] === -1) end++;
    const from = times[at - 1]?.endMs ?? 0;
    const to = end < typed.length ? old[matched[end]!]!.startMs : length;
    times.push(...shared(from, to, end - at, MIN_WORD_MS));
    at = end;
  }

  let previousEnd = -Infinity;
  return typed.map((word, at) => {
    let { startMs, endMs } = times[at]!;
    if (startMs < previousEnd) {
      startMs = previousEnd;
      if (endMs <= startMs) endMs = startMs + MIN_WORD_MS;
    }
    previousEnd = endMs;
    // Held within the caption: a word past its end is drawn at the end, lasting no time.
    const heldStart = clamp(startMs, 0, length);
    // A matched word keeps its mark (#264); a typed one has none.
    const kept = matched[at]!;
    const emphasis = kept >= 0 ? old[kept]!.emphasis : null;
    return { ...word, startMs: heldStart, endMs: clamp(endMs, heldStart, length), emphasis };
  });
}

export interface CaptionSplit {
  first: CaptionWord[];
  second: CaptionWord[];
  /** Where the caption splits, from its start. */
  atMs: number;
}

/**
 * Live's Enter at `caret` in the caption's text (`captionText(words)`): the caption splits before
 * the first word starting at or after the caret, or holding it (so a caret inside a word moves the
 * whole word to the second part); at the very start or after the last word, in the middle. The split
 * is at the first part's last word's end, else at the first part's share of the text's characters,
 * at least 100 ms from either end. The first part keeps its words within its length; the second's
 * keep their place in time, from the split (live's moved its first word to the split, so every mark
 * ran early by the pause before it). Each part keeps its line breaks, its first word on line 0
 * (live's lost them). Null with fewer than two words, or a caption too short to keep 100 ms each
 * side.
 */
export function splitAtCaret(words: readonly CaptionWord[], caret: number, lengthMs: number): CaptionSplit | null {
  const length = Math.round(lengthMs);
  // The words in the text's order, each with where it sits in the text: a line's words joined by
  // spaces, lines by line breaks.
  const placed: { word: CaptionWord; from: number; to: number }[] = [];
  let offset = 0;
  linesOf(words).forEach((line, lineAt) => {
    if (lineAt > 0) offset += 1;
    line.forEach((word, wordAt) => {
      if (wordAt > 0) offset += 1;
      placed.push({ word, from: offset, to: offset + word.text.length });
      offset += word.text.length;
    });
  });
  const count = placed.length;
  if (count < 2 || length < 2 * MIN_PART_MS) return null;

  const found = placed.findIndex(({ from, to }) => from >= caret || (from <= caret && caret < to));
  const cutAt = found > 0 ? found : Math.max(1, Math.floor(count / 2));
  const firstPart = placed.slice(0, cutAt).map(({ word }) => word);
  const secondPart = placed.slice(cutAt).map(({ word }) => word);

  const lastEnd = firstPart[firstPart.length - 1]!.endMs;
  // The text up to the first part's last word, over the whole text.
  const share = offset > 0 ? placed[cutAt - 1]!.to / offset : 0.5;
  const atMs = clamp(Math.round(lastEnd > 0 ? lastEnd : length * share), MIN_PART_MS, length - MIN_PART_MS);

  const firstLine = firstPart[0]!.line;
  const first = firstPart.map((word) => {
    const startMs = clamp(word.startMs, 0, atMs);
    return {
      text: word.text,
      startMs,
      endMs: clamp(word.endMs, startMs, atMs),
      line: word.line - firstLine,
      emphasis: word.emphasis,
    };
  });
  const secondLength = length - atMs;
  const secondLine = secondPart[0]!.line;
  const second = secondPart.map((word) => {
    const startMs = clamp(word.startMs - atMs, 0, secondLength);
    return {
      text: word.text,
      startMs,
      endMs: clamp(word.endMs - atMs, startMs, secondLength),
      line: word.line - secondLine,
      emphasis: word.emphasis,
    };
  });
  return { first, second, atMs };
}
